import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { CheckCircle2, ChefHat, Clock, type LucideIcon } from 'lucide-react'
import { decodeRoles, initializeSession } from '../../lib/auth'
import type { Lang } from '../../lib/i18n'
import { canSeeKitchenTv, fetchKitchenTasks, type KitchenTask } from '../../lib/kitchen-api'
import { Bilingual, fromPair } from '../../components/Bilingual'
import { KITCHEN_LABEL, type KitchenLabelKey } from './kitchen-i18n'
import { kitchenOrderCode } from './kitchen-code'
import { createTvAudio, playBeep, playRing } from './kitchen-tv-sound'

// R285 - the kitchen wall TV at /kitchen/tv: three columns, no chrome, no action buttons. The
// only button it ever renders is the start overlay's, whose tap unlocks audio.

interface Props {
  lang: Lang
  onLangToggle?: () => void
  isOnline?: boolean
  // The cells inject spies here; the module's players are the defaults.
  sounds?: {
    beep: (ctx: AudioContext) => void
    ring: (ctx: AudioContext) => void
  }
}

const POLL_INTERVAL_MS = 20_000
const READY_WINDOW_HOURS = 2

type ColumnKey = 'needs-prep' | 'in-prep' | 'ready'

const COLUMNS: Array<{
  key: ColumnKey
  label: KitchenLabelKey
  Icon: LucideIcon
  head: string
  body: string
}> = [
  { key: 'needs-prep', label: 'tvNeedsPrep', Icon: Clock, head: 'bg-amber-500 text-black', body: 'bg-amber-50' },
  { key: 'in-prep', label: 'tvInPrep', Icon: ChefHat, head: 'bg-blue-600 text-white', body: 'bg-blue-50' },
  { key: 'ready', label: 'tvReady', Icon: CheckCircle2, head: 'bg-green-600 text-white', body: 'bg-green-50' },
]

// PENDING -> needs prep, IN_PROGRESS -> in prep, COMPLETED -> done; BLOCKED sits in in prep
// once it has been started, else in needs prep. CANCELLED never shows.
function columnOf(task: KitchenTask): ColumnKey | null {
  switch (task.status) {
    case 'PENDING':
      return 'needs-prep'
    case 'IN_PROGRESS':
      return 'in-prep'
    case 'BLOCKED':
      return task.startedAt ? 'in-prep' : 'needs-prep'
    case 'COMPLETED':
      return 'ready'
    default:
      return null
  }
}

function reasonLabel(reason: string) {
  const key = `reason${reason}` as KitchenLabelKey
  return KITCHEN_LABEL[key] ?? { en: reason, ar: null }
}

function TvCard({ task }: { task: KitchenTask }) {
  return (
    <article data-tv-task={task.taskId} className="flex gap-4 rounded-xl border bg-white p-4 shadow">
      {task.imageUrl ? (
        <img src={task.imageUrl} alt={task.nameEn} className="h-24 w-24 shrink-0 rounded-lg object-cover" />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="text-2xl font-semibold">
          <Bilingual ar={task.nameAr} en={task.nameEn} />
        </div>
        <div className="flex items-baseline gap-4">
          <span data-tv="quantity" className="text-5xl font-bold">{`x${task.quantity}`}</span>
          <span data-tv="code" dir="ltr" className="text-3xl font-bold">
            {kitchenOrderCode(task.orderId, task.orderCode)}
          </span>
        </div>
        {task.firstName ? <span className="text-xl text-gray-700">{task.firstName}</span> : null}
        {task.status === 'BLOCKED' ? (
          <div data-tv="blocked" className="rounded-md bg-red-600 px-3 py-1 text-xl font-semibold text-white">
            {fromPair(KITCHEN_LABEL.statusBLOCKED, undefined, 'inherit')}
            {task.blockReason ? <> {fromPair(reasonLabel(task.blockReason), undefined, 'inherit')}</> : null}
          </div>
        ) : null}
      </div>
    </article>
  )
}

function idsIn(tasks: KitchenTask[], column: ColumnKey): Set<string> {
  return new Set(tasks.filter((t) => columnOf(t) === column).map((t) => t.taskId))
}

// hh:mm on the wall's own clock, Western digits in either language.
function formatClock(d: Date): string {
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}

function hasNew(now: Set<string>, before: Set<string>): boolean {
  for (const id of now) if (!before.has(id)) return true
  return false
}

export function KitchenTvScreen({ lang, sounds }: Props) {
  const [authState, setAuthState] = useState<'checking' | 'ok' | 'redirect'>('checking')
  const [tasks, setTasks] = useState<KitchenTask[]>([])
  const [armed, setArmed] = useState(false)
  const [blocked, setBlocked] = useState(false)
  // UX form pass bench 4: when the wall last heard from the server, and whether the last poll failed.
  const [lastOk, setLastOk] = useState<Date | null>(null)
  const [stale, setStale] = useState(false)
  const alive = useRef(true)
  const audio = useRef<AudioContext | null>(null)
  const prev = useRef<{ needs: Set<string>; ready: Set<string> } | null>(null)
  const players = useRef({ beep: sounds?.beep ?? playBeep, ring: sounds?.ring ?? playRing })
  players.current = { beep: sounds?.beep ?? playBeep, ring: sounds?.ring ?? playRing }

  useEffect(() => {
    alive.current = true
    initializeSession().then(() => {
      if (!alive.current) return
      setAuthState(canSeeKitchenTv(decodeRoles()) ? 'ok' : 'redirect')
    })
    return () => {
      alive.current = false
    }
  }, [])

  const load = useCallback(async () => {
    try {
      const list = await fetchKitchenTasks({ doneWithinHours: READY_WINDOW_HOURS })
      if (!alive.current) return
      const now = { needs: idsIn(list, 'needs-prep'), ready: idsIn(list, 'ready') }
      const before = prev.current
      const ctx = audio.current
      // Sound only after the start tap, and never on the first load: there is nothing to compare.
      if (ctx && before) {
        if (hasNew(now.needs, before.needs)) players.current.beep(ctx)
        if (hasNew(now.ready, before.ready)) players.current.ring(ctx)
      }
      prev.current = now
      setTasks(list)
      setLastOk(new Date())
      setStale(false)
    } catch {
      // A failed poll keeps what is on screen, and says so: the stale dot turns red.
      if (alive.current) setStale(true)
    }
  }, [])

  useEffect(() => {
    if (authState !== 'ok') return
    void load()
    const id = window.setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [authState, load])

  async function start() {
    const ctx = createTvAudio()
    if (!ctx) {
      setBlocked(true)
      return
    }
    try {
      await ctx.resume()
    } catch {
      // fall through: the state check below decides
    }
    if (ctx.state === 'running') {
      audio.current = ctx
      setBlocked(false)
      setArmed(true)
    } else {
      setBlocked(true)
    }
  }

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  return (
    <div dir={dir} className="min-h-screen w-full bg-gray-100 font-sans">
      <div className="grid min-h-screen w-full grid-cols-3 gap-2 p-2">
        {COLUMNS.map(({ key, label, Icon, head, body }) => (
          <section key={key} data-column={key} className={`flex flex-col gap-2 rounded-xl p-2 ${body}`}>
            <h2 className={`flex items-center gap-3 rounded-lg px-4 py-3 text-4xl font-bold ${head}`}>
              <Icon aria-hidden className="h-10 w-10" />
              {fromPair(KITCHEN_LABEL[label], undefined, 'inherit')}
            </h2>
            {tasks
              .filter((t) => columnOf(t) === key)
              .map((t) => (
                <TvCard key={t.taskId} task={t} />
              ))}
          </section>
        ))}
      </div>

      {lastOk || stale ? (
        <div
          data-tv="freshness"
          role="status"
          className="fixed bottom-3 end-3 z-40 flex items-center gap-3 rounded-full bg-card px-4 py-2 text-xl text-foreground shadow"
        >
          <span
            aria-hidden
            data-tv="stale-dot"
            data-stale={stale ? 'true' : 'false'}
            className={`h-4 w-4 shrink-0 rounded-full ${stale ? 'bg-destructive' : 'bg-sage'}`}
          />
          {stale ? fromPair(KITCHEN_LABEL.tvStale, undefined, 'inherit') : null}
          {lastOk ? (
            <span className="inline-flex items-baseline gap-2">
              {fromPair(KITCHEN_LABEL.tvUpdated, undefined, 'inherit')}
              <span dir="ltr" data-tv="updated-at">
                {formatClock(lastOk)}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}

      {!armed ? (
        <div
          data-tv="start-overlay"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-black/90 p-8 text-center text-white"
        >
          <h1 className="text-5xl font-bold">{fromPair(KITCHEN_LABEL.tvStartTitle, undefined, 'inherit')}</h1>
          <button
            type="button"
            data-tv="start"
            onClick={() => void start()}
            className="rounded-2xl bg-green-600 px-10 py-6 text-4xl font-bold text-white"
          >
            {fromPair(KITCHEN_LABEL.tvStartButton, undefined, 'inherit')}
          </button>
          {blocked ? (
            <p role="alert" className="text-3xl text-amber-300">
              {fromPair(KITCHEN_LABEL.tvAudioBlocked, undefined, 'inherit')}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
